/** @jsx jsx */
import { css, jsx } from "@emotion/react"
import React, { useContext, useEffect, useState } from "react"
import FloatingSelect from "../Elements/Form/FloatingSelect"
import FloatingInteger from "../Elements/Form/FloatingInteger"
import FloatingInput from "../Elements/Form/FloatingInput"
import ButtonLite from "../Elements/ButtonLite"
import NotificationContext from "../../context/Notification/NotificationContext"
import DataContext from "../../context/Data/DataContext"
import FloatingQuantitySelect from "../Elements/Form/FloatingQuantitySelect"
import IconClose from "../Elements/IconClose"
import { PlusSmIcon } from "@heroicons/react/solid"
import UserAllocationPanel from "./UserAllocationPanel"
import AllocationAvailabilitySummary from "./AllocationAvailabilitySummary"
import {
  heldUnitsTotal,
  segmentHeldUnits,
  updateSegmentConfiguredAllocation,
  unallocatedQuantity,
} from "../../helpers/allocationAvailability"

const hasItems = value => Array.isArray(value) && value.length > 0
const getGroupType = item => {
  if (hasItems(item?.field_group_companies) || hasItems(item?.companies))
    return "Retail companies"
  if (hasItems(item?.field_group_stores) || hasItems(item?.stores))
    return "Retail stores"
  if (hasItems(item?.field_group_regions) || hasItems(item?.regions))
    return "Regional retailers"
  if (hasItems(item?.field_group_users) || hasItems(item?.users))
    return "Company users"
  return ""
}

// Returns true when a segment's group is an internal (company-users) group.
const isInternalGroup = (seg, universalUserGroups, myUserGroups) => {
  if (!seg.roles?.length) return false
  const roleId = parseInt(seg.roles[0])
  const uMatch = (universalUserGroups || []).find(
    g => parseInt(g.id) === roleId
  )
  if (uMatch?.users?.length > 0) return true
  const mMatch = (myUserGroups || []).find(g => parseInt(g.id) === roleId)
  if (mMatch?.field_group_users?.length > 0 || mMatch?.users?.length > 0)
    return true
  return false
}

// Normalises the truthy values that per-segment per_user_alloc can carry
// (true | "1" | 1) — boolean from RHF, string from PHP REST, integer from
// Drupal boolean storage.
const isTruthyToggle = v => v === true || v === "1" || v === 1

const Allocations = props => {
  const { toggleNotification } = useContext(NotificationContext)
  const {
    allocationState,
    setAllocationState,
    errors,
    userAllocErrors = new Set(),
    total,
    digital = false,
    submitAttemptCounter = 0,
  } = props
  const { myUserGroups, universalUserGroups } = useContext(DataContext).data
  const [expandedPanels, setExpandedPanels] = useState(new Set())

  const togglePanel = k => {
    setExpandedPanels(prev => {
      const next = new Set(prev)
      if (next.has(k)) next.delete(k)
      else next.add(k)
      return next
    })
  }

  // Check if a specific allocation row targets an internal segment (Company users).
  const isRowInternal = item => {
    if (!item.roles?.length) return false
    const roleId = parseInt(item.roles[0])
    const universalMatch = (universalUserGroups || []).find(
      g => parseInt(g.id) === roleId
    )
    if (universalMatch?.users?.length > 0) return true
    const myMatch = (myUserGroups || []).find(g => parseInt(g.id) === roleId)
    if (myMatch?.field_group_users?.length > 0 || myMatch?.users?.length > 0)
      return true
    return false
  }

  // Auto-expand per-user panels for internal segments.
  useEffect(() => {
    const internalIndices = new Set()
    allocationState.forEach((item, k) => {
      if (isRowInternal(item)) internalIndices.add(k)
    })
    if (internalIndices.size > 0) {
      setExpandedPanels(prev => {
        const next = new Set(prev)
        internalIndices.forEach(k => next.add(k))
        return next
      })
    }
  }, [allocationState.map(a => a.roles?.[0]).join(",")])

  // On a blocked save attempt with allocation errors, expand every errored
  // panel and scroll the first errored segment into view so the rep can see
  // exactly which row needs attention. Triggered by submitAttemptCounter
  // changing in the parent CreateAsset onSubmit guard.
  useEffect(() => {
    if (!submitAttemptCounter || userAllocErrors.size === 0) return
    setExpandedPanels(prev => {
      const next = new Set(prev)
      userAllocErrors.forEach(idx => next.add(idx))
      return next
    })
    const firstIdx = Math.min(...userAllocErrors)
    if (typeof document !== "undefined") {
      const el = document.querySelector(
        `[data-allocation-error="true"][data-allocation-index="${firstIdx}"]`
      )
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth", block: "center" })
      }
    }
    // The counter is the trigger; reading the latest userAllocErrors and
    // allocationState from closure is intentional so we expand and scroll
    // based on the values at the moment the submit attempt was blocked.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitAttemptCounter])

  const groupOptions = [
    ...(Array.isArray(universalUserGroups) ? universalUserGroups : []),
    ...(Array.isArray(myUserGroups) ? myUserGroups : []),
  ].map(item => ({
    value: item.id,
    label: item.title,
    type: getGroupType(item),
  }))

  const groupOptionsById = new Map(
    groupOptions.map(option => [parseInt(option.value), option])
  )

  const addAnother = () => {
    if (parseInt(total) === 0) return

    const defaultAllocation = 0

    setAllocationState(prevState => [
      ...prevState,
      {
        roles: [],
        aloc: defaultAllocation.toString(),
        type: "can_order",
        restriction_number: "5",
        restriction_frequency: "daily",
      },
    ])
  }

  const onRemove = k => {
    if (parseInt(total) === 0) return
    setAllocationState(prev => {
      const next = JSON.parse(JSON.stringify(prev))
      next.splice(k, 1)
      return next
    })
    // Shift expandedPanels indices after removal.
    setExpandedPanels(prev => {
      const next = new Set()
      prev.forEach(idx => {
        if (idx < k) next.add(idx)
        else if (idx > k) next.add(idx - 1)
        // idx === k is dropped.
      })
      return next
    })
  }

  const updateFieldChanged = (stateIndex, fieldName, fieldValue) => {
    if (parseInt(total) === 0) return

    if (fieldName === "aloc") {
      // Check that the units the pools hold do not exceed the total in stock.
      // A per-user pool holds what is left in it, not its configured size, so
      // an exhausted pool no longer blocks handing its freed stock to another
      // segment (useo2o/o2o-backend#414).
      let totalAllocated = 0
      allocationState.forEach((item, k) => {
        if (k !== stateIndex) {
          totalAllocated += segmentHeldUnits(item, { digital })
        }
      })
      totalAllocated += segmentHeldUnits(
        { ...allocationState[stateIndex], aloc: fieldValue },
        { digital }
      )
      if (totalAllocated > parseInt(total)) {
        toggleNotification({
          error: true,
          content: "You've reached the maximum stock available.",
        })

        return
      }

      // Prevent reducing segment qty below sum of per-user allocations.
      if (allocationState[stateIndex]?.user_allocations?.length > 0) {
        const currentUserTotal = allocationState[
          stateIndex
        ].user_allocations.reduce((sum, a) => sum + (parseInt(a.aloc) || 0), 0)
        if (parseInt(fieldValue) < currentUserTotal) {
          toggleNotification({
            error: true,
            content: "Reduce individual user allocations first.",
          })
          return
        }
      }
    }

    setAllocationState(prevState => {
      const newArr = [...prevState]
      newArr[stateIndex] =
        fieldName === "aloc"
          ? updateSegmentConfiguredAllocation(
              newArr[stateIndex],
              fieldValue,
              total
            )
          : { ...newArr[stateIndex], [fieldName]: fieldValue }
      return newArr
    })
  }

  const typesOptions = [
    { value: "can_order", label: "Can Order" },
    { value: "must_request", label: "Must Request" },
  ]

  const frequencyOptions = [
    { value: "daily", label: "Per day" },
    { value: "weekly", label: "Per week" },
    // { value: "biweekly", label: "Biweekly" },
    { value: "monthly", label: "Per month" },
    { value: "yearly", label: "Per year" },
  ]

  return (
    <div className="relative">
      {parseInt(total) === 0 && (
        <div
          className="absolute top-0 left-0 right-0 bottom-0 bg-[rgba(0,0,0,.4)] z-[1] rounded-[8px]"
          css={css`
            backdrop-filter: blur(4px);
          `}
        >
          <div className="bg-white rounded-[8px] px-8 py-10 absolute top-[100px] mx-auto left-0 right-0 w-[50%] shadow-[0_10px_30px_rgba(0,0,0,.2)]">
            <h2 className="text-strong-25 !text-left">Sorry!</h2>
            <p className="text-reg-16 mb-4">
              This item must be in stock before allocation inventory.
            </p>
          </div>
        </div>
      )}
      <div
        className={
          parseInt(total) === 0 ? `p-6 h-[400px] overflow-hidden` : `p-0`
        }
      >
        <p
          css={css`
            font-family: "Jost";
            font-style: normal;
            font-weight: 400;
            font-size: 15px;
            line-height: 20px;
            display: inline-block;
            padding: 0 10px;
            margin: 0px 0 15px;
            letter-spacing: 0.02em;
            color: #717171;
          `}
        >
          Select 1 or more User Segments and assign their Per Segment
          Availability and Order Limits. Allocations are prioritized from most
          specific User Segment to least; Users, Stores, Companies and then
          Regions.
        </p>
        {userAllocErrors.size > 0 && (
          <div
            role="alert"
            className="border-2 border-red-600 bg-red-50 rounded-[8px] p-4 mb-4 ring-2 ring-red-200"
          >
            <p className="text-reg-14 text-red-700 font-bold mb-2">
              {userAllocErrors.size === 1
                ? "1 segment needs attention before you can save"
                : `${userAllocErrors.size} segments need attention before you can save`}
            </p>
            <ul className="text-reg-12 text-red-800 list-disc ml-5">
              {[...userAllocErrors]
                .sort((a, b) => a - b)
                .map(idx => {
                  const seg = allocationState[idx]
                  if (!seg) return null
                  const segRoleId = seg.roles?.[0]
                  const segGroup =
                    segRoleId !== undefined && segRoleId !== null
                      ? groupOptionsById.get(parseInt(segRoleId))
                      : null
                  const groupLabel =
                    segGroup?.label || `Allocation row ${idx + 1}`
                  const segAloc = parseInt(seg.aloc) || 0
                  const userTotal = (seg.user_allocations || []).reduce(
                    (sum, a) => sum + (parseInt(a.aloc) || 0),
                    0
                  )
                  const diff = segAloc - userTotal
                  let detail
                  if (diff > 0) {
                    detail = `${userTotal} of ${segAloc} units assigned — ${diff} more to assign.`
                  } else if (diff < 0) {
                    const over = -diff
                    detail = `${userTotal} assigned, but segment cap is ${segAloc} — remove ${over} below.`
                  } else {
                    detail = `Per-user totals must equal the segment quantity (${segAloc}).`
                  }
                  return (
                    <li key={idx}>
                      <strong>{groupLabel}</strong>: {detail}
                    </li>
                  )
                })}
            </ul>
          </div>
        )}
        <div className="flex flex-col" role="list">
          {allocationState.map((item, k) => {
            const roleId = item.roles?.[0]
            const selectedGroup =
              roleId !== undefined && roleId !== null
                ? groupOptionsById.get(parseInt(roleId))
                : null
            const rowInternal = isRowInternal(item)

            return (
              <div
                key={k}
                role="listitem"
                data-testid={`allocation-segment-${k}`}
                data-allocation-index={k}
                data-allocation-error={
                  userAllocErrors.has(k) ? "true" : undefined
                }
                className={`rounded-[12px] p-5 pr-10 mb-4 relative bg-white ${
                  userAllocErrors.has(k)
                    ? "border-2 border-red-500"
                    : "border border-[#EBEBEB]"
                }`}
              >
                <div className="absolute top-3 right-3">
                  <IconClose
                    callback={() => onRemove(k)}
                    small
                    height={2}
                    width={2}
                  />
                </div>

                {userAllocErrors.has(k) &&
                  (() => {
                    const segAloc = parseInt(item.aloc) || 0
                    const userTotal = (item.user_allocations || []).reduce(
                      (sum, a) => sum + (parseInt(a.aloc) || 0),
                      0
                    )
                    const diff = segAloc - userTotal
                    if (diff > 0) {
                      return (
                        <div className="text-reg-12 text-red-600 font-medium mb-3">
                          Per-user allocation incomplete — assign {diff} more
                          unit{diff === 1 ? "" : "s"} below (currently{" "}
                          {userTotal} of {segAloc}).
                        </div>
                      )
                    }
                    if (diff < 0) {
                      const over = -diff
                      return (
                        <div className="text-reg-12 text-red-600 font-medium mb-3">
                          Per-user allocation exceeds segment quantity — remove{" "}
                          {over} unit{over === 1 ? "" : "s"} below (currently{" "}
                          {userTotal} of {segAloc}).
                        </div>
                      )
                    }
                    return null
                  })()}

                <div className="text-reg-11 uppercase text-[#999] tracking-wider font-semibold mb-3">
                  Availability
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
                  <FloatingSelect
                    label="User Segment"
                    name="User Segment"
                    options={groupOptions}
                    subtitleSelector="type"
                    value={selectedGroup || null}
                    className="!mb-0"
                    onChange={v => {
                      const nextRoles = v?.value ? [v.value] : []
                      updateFieldChanged(k, "roles", nextRoles)
                      // Segment changed -> old per-user UIDs no longer belong
                      // to the new segment. Always clear user_allocations so
                      // alien UIDs don't reach the PATCH payload or trip the
                      // FE completeness validator.
                      updateFieldChanged(k, "user_allocations", [])
                      // When the newly selected segment is not internal, also
                      // clear per_user_alloc so a stale 1 from a prior
                      // internal selection cannot reach the BE (which 400s on
                      // retailer-group segments with per_user_alloc=1).
                      if (!isRowInternal({ roles: nextRoles })) {
                        updateFieldChanged(k, "per_user_alloc", 0)
                      }
                    }}
                    error={
                      !item.roles?.length || errors.has(k)
                        ? { message: "This field is required." }
                        : false
                    }
                    required={true}
                  />
                  <div>
                    <FloatingQuantitySelect
                      flush
                      className="!mb-0"
                      decrement={() => {
                        if (parseInt(item.aloc) > 0)
                          updateFieldChanged(k, "aloc", parseInt(item.aloc) - 1)
                      }}
                      increment={() => {
                        updateFieldChanged(k, "aloc", parseInt(item.aloc) + 1)
                      }}
                      count={item.aloc}
                      label="Configured qty"
                      onChange={v => {
                        if (isNaN(v)) updateFieldChanged(k, "aloc", parseInt(0))
                        else updateFieldChanged(k, "aloc", parseInt(v))
                      }}
                    />
                    <AllocationAvailabilitySummary
                      segment={item}
                      totalStock={total}
                    />
                  </div>
                  <FloatingSelect
                    flush
                    className="!mb-0"
                    label="Type"
                    name="type"
                    options={typesOptions}
                    onChange={v => {
                      updateFieldChanged(k, "type", v.value || null)
                    }}
                    required={true}
                    value={
                      typesOptions
                        .map(i => i.value)
                        .includes(allocationState[k].type)
                        ? typesOptions.filter(
                            f => allocationState[k].type === f.value
                          )[0]
                        : typesOptions[0]
                    }
                  />
                </div>

                {/* W4: Order Limits no longer apply to internal-group
                     segments (sales reps). Render the Frequency / Order
                     Limit Qty controls only for non-internal (retailer)
                     segments. Internal segments still get the per-user
                     allocation drawer toggle below. */}
                {!rowInternal && (
                  <>
                    <div className="text-reg-11 uppercase text-[#999] tracking-wider font-semibold mb-3">
                      Order Limits
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
                      <FloatingSelect
                        flush
                        className="!mb-0"
                        options={frequencyOptions}
                        onChange={v => {
                          updateFieldChanged(
                            k,
                            "restriction_frequency",
                            v.value
                          )
                        }}
                        value={
                          frequencyOptions
                            .map(i => i.value)
                            .includes(allocationState[k].restriction_frequency)
                            ? frequencyOptions.filter(
                                f =>
                                  allocationState[k].restriction_frequency ===
                                  f.value
                              )[0]
                            : frequencyOptions[0]
                        }
                        label="Frequency"
                        required={true}
                      />
                      <FloatingQuantitySelect
                        flush
                        className="!mb-0"
                        decrement={() => {
                          if (parseInt(item.restriction_number) > 0)
                            updateFieldChanged(
                              k,
                              "restriction_number",
                              parseInt(item.restriction_number) - 1
                            )
                        }}
                        increment={() => {
                          updateFieldChanged(
                            k,
                            "restriction_number",
                            parseInt(item.restriction_number) + 1
                          )
                        }}
                        onChange={v => {
                          if (isNaN(v))
                            updateFieldChanged(
                              k,
                              "restriction_number",
                              parseInt(0)
                            )
                          else
                            updateFieldChanged(
                              k,
                              "restriction_number",
                              parseInt(v)
                            )
                        }}
                        count={parseInt(item.restriction_number)}
                        label={"Order Limit Qty"}
                      />
                      <div className="flex flex-col">
                        <span className="text-reg-11 text-[#999]">
                          (per location)
                        </span>
                      </div>
                    </div>
                  </>
                )}

                {/* Phase 3: per-segment per_user_alloc toggle.
                     Each internal-group row carries its own opt-in
                     checkbox. When unchecked the segment uses legacy
                     segment-level distribution; when checked the
                     customize-allocation drawer appears for that row
                     only. Sibling segments are unaffected. */}
                {rowInternal && (
                  <div className="flex flex-col">
                    <label className="text-reg-12 flex items-center gap-2 mb-2">
                      <input
                        type="checkbox"
                        className="h-4 w-4 border-[#EBEBEB] rounded text-[#222] focus:ring-1 focus:ring-offset-2 focus:ring-[#222]"
                        checked={isTruthyToggle(item.per_user_alloc)}
                        onChange={e => {
                          updateFieldChanged(
                            k,
                            "per_user_alloc",
                            e.target.checked ? 1 : 0
                          )
                          if (!e.target.checked) {
                            updateFieldChanged(k, "user_allocations", [])
                          }
                        }}
                      />
                      <span>Enable per-user allocation for this segment</span>
                    </label>
                    {isTruthyToggle(item.per_user_alloc) && (
                      <>
                        <p className="text-reg-11 text-[#999] mt-1 italic">
                          Limit applies per user, not per shipping address.
                        </p>
                        <button
                          type="button"
                          className={`text-reg-11 underline mt-2 cursor-pointer text-left ${
                            userAllocErrors.has(k)
                              ? "text-red-600 font-semibold"
                              : "text-[#2e669b]"
                          }`}
                          onClick={() => togglePanel(k)}
                        >
                          {userAllocErrors.has(k) && (
                            <span className="text-red-600 text-reg-14 font-bold mr-1">
                              *
                            </span>
                          )}
                          {expandedPanels.has(k)
                            ? "▼ Hide per-user allocation"
                            : "▶ Customize per-user allocation"}
                        </button>
                      </>
                    )}
                  </div>
                )}

                {rowInternal &&
                  isTruthyToggle(item.per_user_alloc) &&
                  expandedPanels.has(k) &&
                  item.roles?.length > 0 && (
                    <div className="mt-4">
                      <UserAllocationPanel
                        segmentId={item.roles[0]}
                        segmentAloc={parseInt(item.aloc) || 0}
                        totalStock={total}
                        usageVisible={item.usage_visible === true}
                        userAllocations={item.user_allocations || []}
                        hasError={userAllocErrors.has(k)}
                        onChange={updated =>
                          updateFieldChanged(k, "user_allocations", updated)
                        }
                      />
                    </div>
                  )}
              </div>
            )
          })}
        </div>

        <div className="mt-3 mb-8 max-w-[200px]">
          <ButtonLite
            type="button"
            inline
            callback={() => addAnother()}
            customContent={
              <div className="pt-5 flex cursor-pointer">
                <div className="w-[26px] h-[26px] text-[#2e669b] !hover:text-[#2e669b] rounded-full flex items-center justify-center border-4 border-[#2e669b]">
                  <PlusSmIcon />
                </div>
                <div className="text-[#2e669b] font-semibold text-lg pl-2">
                  Add Allocation
                </div>
              </div>
            }
            colorHover="#2e669b"
          ></ButtonLite>
        </div>
      </div>
    </div>
  )
}

const CreateAssetInventory = ({
  current,
  edit,
  body,
  setBody,
  formErrors,
  setFormErrors,
  segmentErrors,
  setSegmentErrors,
  setRetailersAloc,
  retailersAloc,
  setInternalAloc,
  internalAloc,
  watch,
  submitAttemptCounter = 0,
  // Phase 3: no more asset-level perUserAllocEnabled prop. Each
  // segment carries its own per_user_alloc flag inside allocationState
  // / body.segments, gated per-row in the Allocations sub-component.
}) => {
  const { myUserGroups, universalUserGroups } = useContext(DataContext).data
  const groupOptions = [
    ...(Array.isArray(universalUserGroups) ? universalUserGroups : []),
    ...(Array.isArray(myUserGroups) ? myUserGroups : []),
  ].map(item => ({
    value: item.id,
    label: item.title,
    type: getGroupType(item),
  }))

  const groupOptionsById = new Map(
    groupOptions.map(option => [parseInt(option.value), option])
  )
  const [allocationState, setAllocationState] = useState([])
  const [prevSegmentsO2O, setPrevSegmentsO2O] = useState([])
  const [prevSegmentsInHouse, setPrevSegmentsInHouse] = useState([])
  const [prevSegmentsThirdParty, setPrevSegmentsThirdParty] = useState([])
  const [stock, setStock] = useState(0)
  const [unAloc, setunAloc] = useState(0)
  const [userAllocErrors, setUserAllocErrors] = useState(new Set())

  const warehouseTypes = [
    { label: "O2O Warehouse", value: "o2o" },
    { label: "In-house", value: "in-house" },
    { label: "Third party", value: "third-party" },
  ]
  const warehouseTypeDigital = [
    { label: "Digital Storage", value: "digital-storage" },
  ]

  useEffect(() => {
    if (current && current.segments) {
      setAllocationState(current.segments)
    }
  }, [current])
  useEffect(() => {
    setBody(prevState => ({
      ...prevState,
      segments: allocationState,
    }))
  }, [allocationState])

  useEffect(() => {
    if (body?.fulfillment_type !== "o2o") {
      setBody(prevState => ({
        ...prevState,
        fulfillment: {
          ...prevState.fulfillment,
          name: null,
          stock: null,
        },
      }))
    }
  }, [body?.fulfillment_type])

  useEffect(() => {
    if (!edit) {
      if (watch().field_asset_type === "digital") {
        setBody(prevState => ({
          ...prevState,
          fulfillment_type: "digital-storage",
          stock_o2o: 0,
          stock_in_house: 0,
          stock_third_party: 0,
        }))
      } else {
        setBody(prevState => ({
          ...prevState,
          fulfillment_type: "o2o",
          stock_o2o: 0,
          stock_in_house: 0,
          stock_third_party: 0,
        }))
      }
    }
  }, [watch().field_asset_type])

  useEffect(() => {
    if (!body) {
      return
    }
    const x = []
    let fields = ["notif_total", "notif_rep", "notif_ret", "fulfillment_type"]
    // The stock checks below only run for the in-house and third-party types,
    // which are never digital, so held units need no digital flag here.
    if (body?.fulfillment_type === "in-house")
      fields = [...fields, "fulfillment_name", "stock_in_house"]
    if (body?.fulfillment_type === "third-party")
      fields = [...fields, "fulfillment_name", "stock_third_party"]
    Object.keys(body)
      .filter(f => fields.includes(f))
      .map((item, k) => {
        if (
          item == "stock_in_house" &&
          parseInt(body[item]) < heldUnitsTotal(body?.segments)
        ) {
          x.push(item)
        } else if (
          item == "stock_third_party" &&
          parseInt(body[item]) < heldUnitsTotal(body?.segments)
        ) {
          x.push(item)
        } else if (body[item] === null || body[item] === "") {
          x.push(item)
        }
      })

    // Phase 3: per-user allocation completeness is gated PER SEGMENT.
    // Only segments whose own per_user_alloc is truthy require the
    // sum-equals-segment-aloc check.
    const incompleteAllocIndices = new Set()
    body?.segments?.forEach((seg, idx) => {
      if (
        isTruthyToggle(seg.per_user_alloc) &&
        isInternalGroup(seg, universalUserGroups, myUserGroups) &&
        parseInt(seg.aloc) > 0
      ) {
        const userTotal = (seg.user_allocations || []).reduce(
          (sum, a) => sum + (parseInt(a.aloc) || 0),
          0
        )
        if (userTotal !== parseInt(seg.aloc)) incompleteAllocIndices.add(idx)
      }
    })
    if (incompleteAllocIndices.size > 0) x.push("user_allocation_incomplete")

    const y = new Set()
    body?.segments.map((f, k) => {
      if (f.roles.length === 0) y.add(k)
    })
    setFormErrors(x)
    setSegmentErrors(y)
    setUserAllocErrors(incompleteAllocIndices)
  }, [current, body])

  useEffect(() => {
    if (body?.fulfillment_type === "o2o") setStock(body?.stock_o2o)
    if (body?.fulfillment_type === "in-house") setStock(body?.stock_in_house)
    if (body?.fulfillment_type === "third-party")
      setStock(body?.stock_third_party)
  }, [body])

  useEffect(() => {
    if (stock === "infinite") return
    const n = Number(stock)
    if (!Number.isFinite(n) || n < 0) {
      setunAloc(0)
      return
    }
    let internalaloc = 0
    let retailersaloc = 0
    allocationState.forEach(v => {
      if (!v.roles.length) return
      const option = groupOptionsById.get(parseInt(v.roles[0]))
      if (!option) {
        // The group is not in the loaded lists yet (or is orphaned), so its
        // audience is unknown. Skipping it here folds its allocation into
        // unallocated instead of misreading it as retail (issue #329).
        return
      }
      // A per-user pool counts by what is left in it, not by its configured
      // size (useo2o/o2o-backend#414).
      const held = segmentHeldUnits(v, {
        digital: body?.fulfillment_type === "digital-storage",
      })
      if (option.type === "Company users") internalaloc += held
      else retailersaloc += held
    })
    setRetailersAloc(retailersaloc)
    setInternalAloc(internalaloc)
    setunAloc(unallocatedQuantity(n, internalaloc + retailersaloc))
    // groupOptionsById is rebuilt every render, so depend on its size — it
    // goes from 0 to N once the group lists load and the split can be redone.
  }, [allocationState, stock, groupOptionsById.size, body?.fulfillment_type])
  return (
    <div className="mb-4">
      <span
        // className="text-sm"
        css={css`
          font-family: "Jost";
          font-style: normal;
          font-weight: 400;
          font-size: 15px;
          line-height: 20px;
          display: inline-block;
          padding: 0 10px;
          margin: 0px 0 15px;
          letter-spacing: 0.02em;
          color: #717171;
        `}
      >
        Select where this asset will be stored and fulfilled from, and the total
        quantity you have at that location. The “In Stock Quantity” will be
        automatically updated for all assets stored with and fulfilled by the
        O2O Warehouse.
      </span>
      <div className="gap-5 flex justify-center max-h-[100px]">
        {watch().field_asset_type === "physical" ? (
          <FloatingSelect
            value={
              body?.fulfillment_type
                ? warehouseTypes.filter(
                    f => f.value === body?.fulfillment_type
                  )[0]
                : null
            }
            label="Warehouse Type"
            name="type"
            required={true}
            options={warehouseTypes}
            onChange={e => {
              if (body?.fulfillment_type === "o2o") {
                setPrevSegmentsO2O(allocationState)
              }
              if (body?.fulfillment_type === "in-house") {
                setPrevSegmentsInHouse(allocationState)
              }
              if (body?.fulfillment_type === "third-party") {
                setPrevSegmentsThirdParty(allocationState)
              }

              const obj = { fulfillment_type: e?.value || null }

              if (e?.value === "o2o" && prevSegmentsO2O.length) {
                setAllocationState(prevSegmentsO2O)
              } else if (
                e?.value === "in-house" &&
                prevSegmentsInHouse.length
              ) {
                setAllocationState(prevSegmentsInHouse)
              } else if (
                e?.value === "third-party" &&
                prevSegmentsThirdParty.length
              ) {
                setAllocationState(prevSegmentsThirdParty)
              } else {
                setAllocationState([])
              }

              setBody(prevState => ({
                ...prevState,
                ...obj,
                // fulfillment_type: e?.value || null,
              }))
            }}
            error={
              formErrors.includes("fulfillment_type")
                ? { message: "This field must have a value." }
                : false
            }
          />
        ) : (
          <FloatingSelect
            value={{ label: "Digital Storage", value: "digital-storage" }}
            label="Warehouse Type"
            name="type"
            required={true}
            options={warehouseTypeDigital}
            error={
              formErrors.includes("fulfillment_type")
                ? { message: "This field must have a value." }
                : false
            }
          />
        )}
        {body?.fulfillment_type === "digital-storage" && (
          <>
            <FloatingInteger
              value={"infinite"}
              subdued
              disabled={true}
              label="In Stock Quantity (Digital Storage)"
              name="stock_digital_storage"
              required={false}
              onChange={e => {}}
              type="text"
            />
          </>
        )}
        {body?.fulfillment_type === "o2o" ? (
          <>
            {" "}
            <FloatingInteger
              value={body?.stock_o2o}
              subdued
              disabled={body?.fulfillment_type === "o2o"}
              label="In Stock Quantity (O2O Warehouse)"
              name="stock_o2o"
              required={false}
              onChange={e => {}}
            />
          </>
        ) : (
          <>
            {body?.fulfillment_type === "in-house" && (
              <FloatingInteger
                value={body?.stock_in_house}
                label="In Stock Quantity (In house)"
                name="stock_in_house"
                required={false}
                onChange={e => {
                  setBody(prevState => ({
                    ...prevState,
                    stock_in_house: parseInt(e.target.value) || null,
                  }))
                }}
                error={
                  formErrors.includes("stock_in_house")
                    ? {
                        message:
                          body?.stock_in_house < heldUnitsTotal(body?.segments)
                            ? "Your total allocations equal more than this quantity. Either increase the quantity in-stock, or modify your allocations."
                            : "This field must have a value.",
                      }
                    : false
                }
              />
            )}
            {body?.fulfillment_type === "third-party" && (
              <FloatingInteger
                value={body?.stock_third_party}
                label="In Stock Quantity (Third party)"
                name="stock_third_party"
                required={false}
                onChange={e => {
                  setBody(prevState => ({
                    ...prevState,
                    stock_third_party: parseInt(e.target.value) || null,
                  }))
                }}
                error={
                  formErrors.includes("stock_third_party")
                    ? {
                        message:
                          body?.stock_third_party <
                          heldUnitsTotal(body?.segments)
                            ? "Your total allocations equal more than this quantity. Either increase the quantity in-stock, or modify your allocations."
                            : "This field must have a value.",
                      }
                    : false
                }
              />
            )}
          </>
        )}
      </div>
      <div className="flex mt-6 mb-6 items-end border border-1 border-[#EBEBEB] rounded-[8px] px-4 py-4">
        <div className="mr-6 mb-2">
          <span className="text-strong-20 !leading-[1] mr-1">{stock}</span>
          <span>in stock</span>
        </div>
        <div className="grow flex items-center">
          <div
            className={`w-[${
              stock === 0 ? 33.3 : (internalAloc / stock) * 100
            }%] mr-1`}
            style={{
              color: "#0033A1",
              width: `${stock === 0 ? 33.3 : (internalAloc / stock) * 100}%`,
              minWidth: "11%",
            }}
          >
            <div className="text-[14px]">for Internal</div>
            <div className="w-[100%] rounded-l-lg h-[40px] bg-[#0033A1] text-center text-white p-[7px]">
              {internalAloc}
            </div>
          </div>
          <div
            className={`w-[${
              stock === 0 ? 33.3 : (retailersAloc / stock) * 100
            }%] mr-1`}
            style={{
              color: "#009D4F",
              width: `${stock === 0 ? 33.3 : (retailersAloc / stock) * 100}%`,
              minWidth: "11%",
            }}
          >
            <div className="text-[14px]">for Retailers</div>
            <div className="w-[100%] h-[40px] bg-[#009D4F] text-center text-white p-[7px]">
              {retailersAloc}
            </div>
          </div>
          <div
            className={`w-[${stock === 0 ? 33.3 : (unAloc / stock) * 100}%] `}
            style={{
              color: "rgba(0,0,0,.3)",
              width: `${stock === 0 ? 33.3 : (unAloc / stock) * 100}%`,
            }}
          >
            <div className="text-[14px]">Unallocated</div>
            <div
              className="w-[100%] rounded-r-lg h-[40px] bg-[#EBEBEB] text-center p-[7px]"
              style={{ color: "rgba(0,0,0,.3)" }}
            >
              {unAloc}
            </div>
          </div>
        </div>
      </div>
      <Allocations
        total={stock}
        digital={body?.fulfillment_type === "digital-storage"}
        errors={segmentErrors}
        userAllocErrors={userAllocErrors}
        allocationState={allocationState}
        setAllocationState={setAllocationState}
        submitAttemptCounter={submitAttemptCounter}
      />
    </div>
  )
}

export default CreateAssetInventory
